/**
 * Recording a question and getting the words back (UC-A6).
 *
 * ## The recording never leaves this function except to be transcribed
 *
 * It is held as a `Blob` for one request and dropped. Nothing writes it to storage, nothing keeps
 * it after the transcript arrives, and the microphone track is stopped explicitly rather than left
 * to garbage collection — a browser tab with a live track shows a recording indicator, and an
 * employee who sees one after they stopped speaking is right to distrust it.
 *
 * ## Permission is asked for when the button is pressed, not on load
 *
 * A page that asks for the microphone as it opens gets denied by reflex, and in some managed
 * browsers the denial is permanent. It is requested on the first press, when the employee has just
 * expressed the intent.
 *
 * ## It stops itself
 *
 * A recording left running because somebody was called away is an open microphone in a branch. The
 * deployment's own ceiling is enforced here as well as on the server, and the hook stops at it.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { transcribe } from "../api/client";
import { ApiError } from "../api/client";
import type { Transcript } from "../api/types";

export type DictationState = "idle" | "recording" | "transcribing";

export interface UseDictationResult {
  state: DictationState;
  /** Set when the last attempt failed, in words meant for the employee. */
  error: string | null;
  /** Whether this browser can record at all. False on an insecure origin. */
  supported: boolean;
  start: () => Promise<void>;
  stop: () => void;
  /** Drop a recording in progress without transcribing it. */
  cancel: () => void;
}

export interface UseDictationOptions {
  /** Called with the words. Filling the composer is the caller's job; this never sends. */
  onTranscript: (transcript: Transcript) => void;
  /** Ceiling from the deployment. The hook stops itself here. */
  maxSeconds: number;
  language?: string;
  sessionId?: string | null;
  /** Localised messages, so this file holds no user-visible English. */
  messages: {
    denied: string;
    unsupported: string;
    failed: string;
    tooQuiet: string;
  };
}

/**
 * Preferred container, in order.
 *
 * Opus in WebM everywhere it exists — it is a tenth the size of WAV for speech, which matters on a
 * branch connection. Safari produces MP4/AAC and nothing else, hence the fallback.
 */
const CONTAINERS = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];

function pickContainer(): string {
  if (typeof MediaRecorder === "undefined") return "";
  return CONTAINERS.find((type) => MediaRecorder.isTypeSupported?.(type)) ?? "";
}

export function useDictation({
  onTranscript,
  maxSeconds,
  language,
  sessionId,
  messages,
}: UseDictationOptions): UseDictationResult {
  const [state, setState] = useState<DictationState>("idle");
  const [error, setError] = useState<string | null>(null);

  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const stopTimer = useRef<number | undefined>(undefined);
  // Set when the employee cancels, so the `stop` handler knows to drop the audio rather than send
  // it — `MediaRecorder.stop()` fires the same event either way.
  const cancelled = useRef(false);

  const supported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined";

  /** Stop the track, not just the recorder. A live track is a recording indicator in the tab. */
  const release = useCallback(() => {
    window.clearTimeout(stopTimer.current);
    recorder.current?.stream.getTracks().forEach((track) => track.stop());
    recorder.current = null;
    chunks.current = [];
  }, []);

  useEffect(() => release, [release]);

  const start = useCallback(async () => {
    setError(null);
    if (!supported) {
      setError(messages.unsupported);
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          // Speech, in a branch, on a headset. All three help and none of them costs anything.
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch {
      // Denied, or no device. One message for both: the employee's next step is the same, and
      // distinguishing them tells a page more about the machine than it needs.
      setError(messages.denied);
      return;
    }

    const mimeType = pickContainer();
    const media = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorder.current = media;
    chunks.current = [];
    cancelled.current = false;

    media.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.current.push(event.data);
    };

    media.onstop = () => {
      const collected = chunks.current;
      const type = media.mimeType || mimeType || "audio/webm";
      release();

      if (cancelled.current) {
        setState("idle");
        return;
      }

      const audio = new Blob(collected, { type });
      // A press-and-release with nothing in between. Not an error — the employee gets a nudge
      // rather than a failure.
      if (audio.size < 1024) {
        setState("idle");
        setError(messages.tooQuiet);
        return;
      }

      setState("transcribing");
      void (async () => {
        try {
          const heard = await transcribe(audio, {
            ...(language ? { language } : {}),
            ...(sessionId ? { sessionId } : {}),
          });
          if (!heard.text.trim()) {
            setError(messages.tooQuiet);
          } else {
            onTranscript(heard);
          }
        } catch (caught) {
          // The server's own sentence where there is one — "the speech service is unavailable,
          // please type the question instead" tells the employee what to do next, and a generic
          // failure does not.
          setError(
            caught instanceof ApiError && typeof caught.detail === "string"
              ? caught.detail
              : messages.failed,
          );
        } finally {
          setState("idle");
        }
      })();
    };

    media.start();
    setState("recording");
    // The deployment's ceiling, enforced here as well as on the server. A recording left running
    // because somebody was called away is an open microphone in a branch.
    stopTimer.current = window.setTimeout(() => media.stop(), maxSeconds * 1000);
  }, [supported, messages, language, sessionId, maxSeconds, onTranscript, release]);

  const stop = useCallback(() => {
    if (recorder.current?.state === "recording") recorder.current.stop();
  }, []);

  const cancel = useCallback(() => {
    cancelled.current = true;
    if (recorder.current?.state === "recording") {
      recorder.current.stop();
    } else {
      release();
      setState("idle");
    }
  }, [release]);

  return { state, error, supported, start, stop, cancel };
}
